import { LogOut, User, Settings, Phone, PhoneIncoming, PhoneOff, X, MessageSquare, ExternalLink, Building2, UserCircle, Target, CalendarDays, LifeBuoy, Mail, Sparkles, Bot, Brain, Zap, Shield, Star, Ticket, Coins } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StorageQuotaWidget } from '@easyflow/ui-frontend';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@easyflow/ui-frontend/components/ui/popover';
import { ThemeToggle } from '@easyflow/ui-frontend/components/ui/theme-toggle';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import TokenUsageWidget from './TokenUsageWidget';
import ActiveVideoCallIndicator from './ActiveVideoCallIndicator';
import type { StorageQuotaSnapshot } from '@easyflow/shared';
import {
  useMobileHeaderActions,
  MOBILE_ACTION_ICON,
  type MobileHeaderActionId,
} from '@/lib/mobileHeaderActions';
import { useMobileShortcutAvailability } from '@/lib/useMobileShortcutAvailability';
import { useAuth } from '@/contexts/AuthContext';
import { useSipPhone } from '@/contexts/SipPhoneContext';
import { cn } from '@/lib/utils';
import { apiClient } from '@/lib/api';
import { usePersonalAssistantAccess } from '@/contexts/PersonalAssistantAccessContext';
import ProfileModal from '@/components/profile/ProfileModal';
import HRClockWidget from '@/components/hr/HRClockWidget';
import PersonalAssistantChat from '@/components/ai/PersonalAssistantChat';
import QuickTicketDialog from '@/components/support/QuickTicketDialog';

interface HeaderProps {
  onMenuClick?: () => void;
}

export default function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showIncomingPopup, setShowIncomingPopup] = useState(false);
  const { assistant: myAssistant, sectionRequest } = usePersonalAssistantAccess();
  const [showAssistantChat, setShowAssistantChat] = useState(false);
  const [assistantUnreadCount, setAssistantUnreadCount] = useState(0);
  const [quickTicketOpen, setQuickTicketOpen] = useState(false);
  const [quickTicketUrl, setQuickTicketUrl] = useState('');
  
  const sipPhone = useSipPhone();
  const { 
    isRegistered, 
    fsIsRegistered,
    isInCall, 
    state: sipState, 
    callInfo, 
    activeExtension,
    openPhone, 
    answer, 
    hangup,
    isOpen,
    recognizedCustomer
  } = sipPhone;

  // Task #2569 — il badge riflette la verita` backend (fsIsRegistered) OR lo
  // stato locale sip.js: dopo sleep/wake il softphone puo` essere ancora
  // registrato lato FreeSWITCH mentre il client sta ri-validando il transport.
  const sipLinkUp = isRegistered || fsIsRegistered;

  const getCustomerTypeBadge = () => {
    if (!recognizedCustomer) return null;
    const type = recognizedCustomer.type;
    const configs: Record<string, { label: string; className: string; icon: React.ReactNode }> = {
      b2c: { 
        label: 'Contatto', 
        className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300',
        icon: <UserCircle className="h-3 w-3" />
      },
      b2b: { 
        label: 'Azienda', 
        className: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300',
        icon: <Building2 className="h-3 w-3" />
      },
      lead: { 
        label: 'Lead', 
        className: 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-300',
        icon: <Target className="h-3 w-3" />
      }
    };
    const config = configs[type] || configs.b2c;
    return (
      <Badge className={`${config!.className!} text-xs font-medium flex items-center gap-1`}>
        {config!.icon!}
        {config!.label!}
      </Badge>
    );
  };

  const getCustomerProfileUrl = () => {
    if (!recognizedCustomer || !tenantSlug) return null;
    const { type, id } = recognizedCustomer;
    if (type === 'b2c') return `/${tenantSlug}/crm?tab=contacts&id=${id}`;
    if (type === 'b2b') return `/${tenantSlug}/crm?tab=companies&id=${id}`;
    if (type === 'lead') return `/${tenantSlug}/crm?tab=leads&id=${id}`;
    return null;
  };

  useEffect(() => {
    if (!sectionRequest || !myAssistant?.is_active) return;
    setShowAssistantChat(true);
    setAssistantUnreadCount(0);
  }, [sectionRequest, myAssistant?.id, myAssistant?.is_active]);

  const getAssistantIcon = () => {
    const iconMap: Record<string, React.ReactNode> = {
      bot: <Bot className="h-5 w-5" />,
      sparkles: <Sparkles className="h-5 w-5" />,
      brain: <Brain className="h-5 w-5" />,
      zap: <Zap className="h-5 w-5" />,
      shield: <Shield className="h-5 w-5" />,
      star: <Star className="h-5 w-5" />
    };
    return iconMap[myAssistant?.avatar_icon || 'sparkles'] || <Sparkles className="h-5 w-5" />;
  };

  // Real-time clock
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (sipState === 'incoming') {
      setShowIncomingPopup(true);
    } else {
      setShowIncomingPopup(false);
    }
  }, [sipState]);

  const getSipIconColor = () => {
    if (sipState === 'incoming') return 'text-green-500 animate-pulse';
    if (isInCall) return 'text-green-600';
    if (sipLinkUp) return 'text-blue-500';
    return 'text-gray-400';
  };

  const handleSipIconClick = () => {
    if (sipState === 'incoming') {
      setShowIncomingPopup(true);
    } else {
      openPhone();
    }
  };

  useEffect(() => {
    const handleSidebarStateChange = (event: CustomEvent) => {
      setSidebarCollapsed(event.detail.collapsed);
    };

    window.addEventListener('sidebar-state-change', handleSidebarStateChange as EventListener);
    return () => {
      window.removeEventListener('sidebar-state-change', handleSidebarStateChange as EventListener);
    };
  }, []);

  // ⌘K / Ctrl+K opens the global search.
  // On desktop the inline GlobalSearch input is visible, so we focus it via DOM
  // lookup (GlobalSearch.tsx must remain unchanged). On mobile we fall back to
  // dispatching the existing `open-global-search` event which opens the overlay.
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        const target = e.target as HTMLElement | null;
        const isInsideEditable = target && (
          target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable
        );
        if (isInsideEditable) return;
        e.preventDefault();
        const desktopInput = document.querySelector<HTMLInputElement>(
          'input[placeholder="Cerca cliente, lead, azienda..."]'
        );
        if (desktopInput && desktopInput.offsetParent !== null) {
          desktopInput.focus();
          desktopInput.select();
        } else {
          window.dispatchEvent(new CustomEvent('open-global-search'));
        }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  useEffect(() => {
    const handleOpenAssistant = () => {
      if (myAssistant && myAssistant.is_active) {
        setShowAssistantChat(true);
        setAssistantUnreadCount(0);
      }
    };
    window.addEventListener('open-personal-assistant', handleOpenAssistant);
    return () => {
      window.removeEventListener('open-personal-assistant', handleOpenAssistant);
    };
  }, [myAssistant]);

  // La barra mobile in basso può chiedere di aprire il dialog del ticket
  // rapido (lo stesso che oggi è ancorato all'icona "ticket" dell'header).
  useEffect(() => {
    const handleOpenQuickTicket = () => {
      setQuickTicketUrl(window.location.pathname);
      setQuickTicketOpen(true);
    };
    window.addEventListener('open-quick-ticket', handleOpenQuickTicket);
    return () => {
      window.removeEventListener('open-quick-ticket', handleOpenQuickTicket);
    };
  }, []);

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    if (user?.firstName) {
      return user.firstName.charAt(0).toUpperCase();
    }
    return 'U';
  };

  const getUserDisplayName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    if (user?.firstName) {
      return user.firstName;
    }
    return 'Utente';
  };

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'super_admin':
        return 'Super Administrator';
      case 'admin':
        return 'Tenant Admin';
      case 'user':
        return 'User';
      default:
        return 'User';
    }
  };

  // User-customizable quick actions shown ONLY in the mobile header.
  // Il catalogo e la persistenza vivono in `lib/mobileHeaderActions.ts` e
  // sono CONDIVISI con la barra mobile inferiore: lo stesso ID renderizza
  // la stessa icona Lucide (`MOBILE_ACTION_ICON`) ed è gated dalle stesse
  // feature-flag (`useMobileShortcutAvailability`). L'utente può scegliere
  // 3-5 voci dal pannello Settings → "Header mobile".
  const mobileActionIdsRaw = useMobileHeaderActions(user?.id);
  const { filterIds } = useMobileShortcutAvailability();
  const mobileActionIds = filterIds(mobileActionIdsRaw);

  // Helper per le voci "semplici" del catalogo condiviso che si limitano a
  // navigare verso una rotta: usa l'icona del map condiviso così header e
  // bottom nav restano graficamente identiche.
  const renderSimpleNavAction = (
    id: MobileHeaderActionId,
    label: string,
    href: string,
  ) => {
    const Icon = MOBILE_ACTION_ICON[id];
    return (
      <Button
        key={id}
        variant="ghost"
        size="sm"
        className="p-2"
        onClick={() => navigate(href)}
        title={label}
        aria-label={label}
      >
        <Icon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
      </Button>
    );
  };

  const renderMobileAction = (id: MobileHeaderActionId) => {
    switch (id) {
      case 'calendar':
        return (
          <Button
            key="calendar"
            variant="ghost"
            size="sm"
            className="p-2"
            onClick={() => navigate(`/${tenantSlug}/calendar`)}
            title="Calendario"
            aria-label="Calendario"
          >
            <CalendarDays className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Button>
        );
      case 'messaging-chat':
        // Mobile-only: la shortcut "Chat" porta alla chat interna del team
        // (`/messenger`) e NON alla chat clienti CRM (`/messaging/chat`).
        return (
          <Button
            key="messaging-chat"
            variant="ghost"
            size="sm"
            className="p-2"
            onClick={() => navigate(`/${tenantSlug}/messenger`)}
            title="Chat interna"
            aria-label="Chat interna"
          >
            <MessageSquare className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Button>
        );
      case 'messaging-email':
        return (
          <Button
            key="messaging-email"
            variant="ghost"
            size="sm"
            className="p-2"
            onClick={() => navigate(`/${tenantSlug}/messaging/email`)}
            title="Email"
            aria-label="Email"
          >
            <Mail className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Button>
        );
      case 'quick-ticket':
        return (
          <Button
            key="quick-ticket"
            variant="ghost"
            size="sm"
            className="p-2"
            onClick={() => {
              setQuickTicketUrl(window.location.pathname);
              setQuickTicketOpen(true);
            }}
            title="Apri ticket"
            aria-label="Apri ticket"
          >
            <Ticket className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Button>
        );
      case 'theme-toggle':
        return (
          <div key="theme-toggle" className="flex items-center">
            <ThemeToggle />
          </div>
        );
      case 'ai-assistant':
        if (!myAssistant || !myAssistant.is_active) return null;
        return (
          <Button
            key="ai-assistant"
            variant="ghost"
            size="sm"
            className="relative p-2"
            onClick={() => {
              setShowAssistantChat(!showAssistantChat);
              if (!showAssistantChat) setAssistantUnreadCount(0);
            }}
            title={myAssistant.display_name || myAssistant.name}
            aria-label={myAssistant.display_name || myAssistant.name}
          >
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ color: myAssistant.color_theme || '#8B5CF6' }}
            >
              {getAssistantIcon()}
            </div>
            {assistantUnreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center px-1 border border-white">
                {assistantUnreadCount > 9 ? '9+' : assistantUnreadCount}
              </span>
            )}
          </Button>
        );
      case 'token-usage':
        return (
          <div key="token-usage" className="flex items-center">
            <TokenUsageWidget />
          </div>
        );
      case 'dashboard':
        return renderSimpleNavAction('dashboard', 'Dashboard', `/${tenantSlug}/dashboard`);
      case 'sip-phone': {
        // Apre lo stesso dialer SIP usato dal resto dell'app (è il singleton
        // montato in Layout). Pallino verde se registrato/in chiamata.
        const Icon = MOBILE_ACTION_ICON['sip-phone'];
        const pulse = sipPhone.isInCall || sipPhone.isRegistered || sipPhone.fsIsRegistered;
        return (
          <Button
            key="sip-phone"
            variant="ghost"
            size="sm"
            className="relative p-2"
            onClick={() => sipPhone.openPhone()}
            title="Telefono SIP"
            aria-label="Telefono SIP"
          >
            <Icon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
            {pulse && (
              <span
                aria-hidden
                className="absolute top-1 right-1 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white dark:ring-slate-900 animate-pulse"
              />
            )}
          </Button>
        );
      }
      case 'notifications': {
        // Toggle del pannello notifiche: l'unica istanza vive nel
        // NotificationBell montato a destra dell'header. Comunichiamo via
        // window event così non duplichiamo il Popover.
        const Icon = MOBILE_ACTION_ICON['notifications'];
        return (
          <Button
            key="notifications"
            variant="ghost"
            size="sm"
            className="p-2"
            onClick={() => window.dispatchEvent(new CustomEvent('toggle-notifications-panel'))}
            title="Notifiche"
            aria-label="Notifiche"
          >
            <Icon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </Button>
        );
      }
      case 'wiki':
        return renderSimpleNavAction('wiki', 'Wiki aziendale', `/${tenantSlug}/company-wiki`);
      case 'feed':
        return renderSimpleNavAction('feed', 'Feed', `/${tenantSlug}/messenger/feed`);
      case 'ai-tools':
        return renderSimpleNavAction('ai-tools', 'AI Tools', `/${tenantSlug}/ai-tools`);
      default:
        return null;
    }
  };

  return (
    <>
      <header className="glass-header sticky top-0 z-30 px-4 sm:px-6 py-2 sm:py-3 border-b border-white/20 shadow-lg w-full">
        {/* Mobile: griglia [1fr auto 1fr] → spacer sx invisibile, shortcut perfettamente
            centrate nella colonna auto, avatar/bell ancorati a destra.
            Desktop (md+): torna a flex justify-between con left/center/right come prima. */}
        <div className="grid grid-cols-[1fr_auto_1fr] md:flex md:justify-between items-center h-10 sm:h-12 w-full">
          {/* Spacer mobile sx (vuoto): garantisce simmetria con la colonna destra
              così la sezione centrale resta inchiodata al centro. Su desktop sparisce. */}
          <div className="md:hidden" aria-hidden="true" />

          {/* Left side - Search (desktop only). Su mobile il drawer è gestito dal
              tab "Altro" della bottom nav, non più dall'hamburger nell'header. */}
          <div className="hidden md:flex items-center space-x-2 sm:space-x-3 md:flex-1">
              <GlobalSearch className="w-64 lg:w-80" />
            </div>

            {/* Center - Digital Clock (desktop) */}
            <div className="hidden md:flex flex-col items-center text-center flex-shrink-0 px-4">
              <span className="text-sm font-mono font-semibold text-gray-700 dark:text-gray-200 tracking-wide">
                {currentTime.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
              <span className="text-[10px] text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                {currentTime.toLocaleDateString('it-IT', { weekday: 'short', day: '2-digit', month: 'short' })}
              </span>
            </div>

            {/* Center - Mobile quick actions.
                Vivono nella colonna `auto` della griglia mobile, tra due colonne `1fr`
                simmetriche, così le icone restano perfettamente centrate. */}
            <div className="flex md:hidden items-center justify-center flex-shrink-0 gap-1 px-2">
              {mobileActionIds.map(renderMobileAction)}
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center justify-end space-x-2 sm:space-x-4 md:flex-1">
              {myAssistant && myAssistant.is_active && (
                <div className="hidden md:flex items-center">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="relative p-1.5 hover:scale-105 transition-all duration-200"
                          onClick={() => {
                            setShowAssistantChat(!showAssistantChat);
                            if (!showAssistantChat) setAssistantUnreadCount(0);
                          }}
                        >
                          <div
                            className={cn(
                              "relative w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all",
                              showAssistantChat && "drop-shadow-lg"
                            )}
                            style={{
                              color: myAssistant.color_theme || '#8B5CF6',
                              borderColor: myAssistant.color_theme || '#8B5CF6',
                              backgroundColor: showAssistantChat ? (myAssistant.color_theme || '#8B5CF6') + '15' : 'transparent',
                            }}
                          >
                            {getAssistantIcon()}
                          </div>
                          {assistantUnreadCount > 0 && (
                            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1 border-2 border-white">
                              {assistantUnreadCount > 9 ? '9+' : assistantUnreadCount}
                            </span>
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{myAssistant.display_name || myAssistant.name}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              )}
              <div className="hidden md:flex items-center">
                <ActiveVideoCallIndicator />
              </div>
              <div className="hidden md:flex items-center">
                <TokenUsageWidget />
              </div>
              {/* Task #1237 — Storage quota widget (R2 + DB) */}
              <div className="hidden md:flex items-center">
                <StorageQuotaWidget
                  fetchSnapshot={async () => {
                    const r = await apiClient.get<StorageQuotaSnapshot>('/storage/quota/me');
                    return r.data ?? null;
                  }}
                  onNavigateDetail={() => navigate(`/${tenantSlug}/settings?tab=storage`)}
                />
              </div>
              <div className="hidden md:flex items-center">
                <ThemeToggle />
              </div>

              {/* SIP Phone Icon - Direct click opens phone, Popover only for incoming calls */}
              {activeExtension && (
                <>
                  {/* Incoming call popover */}
                  <Popover open={showIncomingPopup && sipState === 'incoming'} onOpenChange={setShowIncomingPopup}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={cn(
                          "relative hover:bg-white/50 hover:scale-105 transition-all duration-200 p-2",
                          isOpen && "bg-blue-100 dark:bg-blue-900/30"
                        )}
                        onClick={handleSipIconClick}
                      >
                        {sipState === 'incoming' ? (
                          <PhoneIncoming className={cn("h-5 w-5", getSipIconColor())} />
                        ) : (
                          <Phone className={cn("h-5 w-5", getSipIconColor())} />
                        )}
                        {isInCall && (
                          <span className="absolute -top-1 -right-1 h-3 w-3 bg-green-500 rounded-full animate-pulse" />
                        )}
                        {isRegistered && !isInCall && (
                          <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 bg-blue-500 rounded-full" />
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="end">
                      <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/30 dark:to-green-800/30 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <PhoneIncoming className="h-6 w-6 text-green-600 animate-bounce" />
                            <span className="font-semibold text-green-800 dark:text-green-200">
                              Chiamata in arrivo
                            </span>
                          </div>
                          {getCustomerTypeBadge()}
                        </div>
                        
                        {/* Customer CRM Info */}
                        {recognizedCustomer ? (
                          <div className="mb-3">
                            <p className="text-lg font-bold text-gray-900 dark:text-white">
                              {recognizedCustomer.name}
                            </p>
                            <p className="text-sm font-mono text-gray-600 dark:text-gray-300">
                              {callInfo?.remoteNumber || 'Sconosciuto'}
                            </p>
                            {recognizedCustomer.company_name && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                                <Building2 className="h-3 w-3" />
                                {recognizedCustomer.company_name}
                              </p>
                            )}
                            {getCustomerProfileUrl() && (
                              <button
                                className="mt-2 flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                                onClick={() => {
                                  const url = getCustomerProfileUrl();
                                  if (url) navigate(url);
                                }}
                                title="Vai al profilo CRM"
                              >
                                <ExternalLink className="h-3.5 w-3.5" />
                                <span>Profilo</span>
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="mb-3">
                            <p className="text-xl font-mono font-bold text-gray-900 dark:text-white">
                              {callInfo?.remoteNumber || 'Sconosciuto'}
                            </p>
                            {callInfo?.remoteName && (
                              <p className="text-sm text-gray-600 dark:text-gray-300">
                                {callInfo.remoteName}
                              </p>
                            )}
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 italic">
                              Contatto non presente nel CRM
                            </p>
                          </div>
                        )}
                        
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                            onClick={() => {
                              answer();
                              setShowIncomingPopup(false);
                              openPhone();
                            }}
                          >
                            <Phone className="h-4 w-4 mr-1" />
                            Rispondi
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            className="flex-1"
                            onClick={() => {
                              hangup();
                              setShowIncomingPopup(false);
                            }}
                          >
                            <PhoneOff className="h-4 w-4 mr-1" />
                            Rifiuta
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                </>
              )}

              {/* Calendar Shortcut */}
              <div className="hidden md:flex">
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative hover:bg-white/50 hover:scale-105 transition-all duration-200 p-2"
                  onClick={() => navigate(`/${tenantSlug}/calendar`)}
                  title="Calendario"
                >
                  <CalendarDays className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </Button>
              </div>

              {/* Chat Shortcut */}
              <div className="hidden md:flex">
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative hover:bg-white/50 hover:scale-105 transition-all duration-200 p-2"
                  onClick={() => navigate(`/${tenantSlug}/messaging/chat`)}
                  title="Messaggi"
                >
                  <MessageSquare className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </Button>
              </div>

              {/* Email Shortcut */}
              <div className="hidden md:flex">
                <Button
                  variant="ghost"
                  size="sm"
                  className="relative hover:bg-white/50 hover:scale-105 transition-all duration-200 p-2"
                  onClick={() => navigate(`/${tenantSlug}/messaging/email`)}
                  title="Email"
                >
                  <Mail className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                </Button>
              </div>

              <div className="hidden md:flex">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="relative hover:bg-white/50 hover:scale-105 transition-all duration-200 p-2"
                        onClick={() => {
                          setQuickTicketUrl(window.location.pathname);
                          setQuickTicketOpen(true);
                        }}
                      >
                        <Ticket className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Apri ticket supporto</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {/* Mobile quick actions are rendered nel container centrale
                  in alto (vedi "Center - Mobile quick actions") per restare
                  perfettamente centrati nell'header su mobile. */}

              <NotificationBell />

              {/* User Avatar with Dropdown */}
              {user && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="relative h-10 w-10 rounded-full p-0 hover:scale-105 transition-all duration-200"
                    >
                      {user.avatarUrl ? (
                        <img 
                          src={user.avatarUrl} 
                          alt="Avatar" 
                          className="h-10 w-10 rounded-full object-cover shadow-lg ring-2 ring-white/50 dark:ring-gray-700/50"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg ring-2 ring-white/50 dark:ring-gray-700/50">
                          <span className="text-sm font-semibold text-white">
                            {getUserInitials()}
                          </span>
                        </div>
                      )}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{getUserDisplayName()}</p>
                        <p className="text-xs leading-none text-muted-foreground">
                          {getRoleLabel()}
                        </p>
                        {user.email && (
                          <p className="text-xs leading-none text-muted-foreground mt-1">
                            {user.email}
                          </p>
                        )}
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="cursor-pointer"
                      onClick={() => setShowProfileModal(true)}
                    >
                      <User className="mr-2 h-4 w-4" />
                      <span>Profilo</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="cursor-pointer"
                      onClick={() => navigate(`/${tenantSlug}/settings`)}
                    >
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Impostazioni</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      className="cursor-pointer"
                      onClick={() => navigate(`/${tenantSlug}/support`)}
                    >
                      <LifeBuoy className="mr-2 h-4 w-4" />
                      <span>I miei Ticket</span>
                    </DropdownMenuItem>
                    <HRClockWidget />
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-900/20"
                      onClick={async () => { await logout(); navigate(`/${tenantSlug}/login`); }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      <span>Esci</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
          </div>
        </div>
      </header>

      <ProfileModal 
        isOpen={showProfileModal} 
        onClose={() => setShowProfileModal(false)}
      />

      {myAssistant && myAssistant.is_active && (
        <PersonalAssistantChat
          assistantId={myAssistant.id}
          assistantName={myAssistant.display_name || myAssistant.name}
          assistantIcon={myAssistant.avatar_icon || 'sparkles'}
          assistantColor={myAssistant.color_theme || '#8B5CF6'}
          assistantDescription={myAssistant.description}
          userFirstName={myAssistant.assigned_user_first_name}
          open={showAssistantChat}
          sectionRequest={sectionRequest}
          onClose={() => setShowAssistantChat(false)}
          onUnreadCountChange={setAssistantUnreadCount}
        />
      )}

      <QuickTicketDialog
        open={quickTicketOpen}
        onOpenChange={setQuickTicketOpen}
        capturedUrl={quickTicketUrl}
      />
    </>
  );
}
